export const H1B_DEFAULT_TABLE_CODES = ["121Y006", "121Y013", "121Y002", "121Y015", "121Y011"] as const;
export const H1B_TABLE_TERMS = [
  "가중평균금리",
  "주택담보",
  "가계대출",
  "가계신용",
  "소비자동향",
  "소비자심리",
  "기대인플레이션",
  "주택가격전망",
  "예금은행"
] as const;
export const H1B_ITEM_TERMS = [
  "주택담보",
  "잔액",
  "고정금리",
  "변동금리",
  "주기형",
  "주택가격전망",
  "기대인플레이션",
  "가계대출"
] as const;

export const H1B_DESCENDANT_ROOT_TERMS = [
  "가중평균금리",
  "고정 및 변동금리대출 비중",
  "가계신용",
  "예금취급기관 가계대출",
  "소비자동향"
] as const;

export const H1B_MAX_AUDIT_TABLES = 48;

export interface EcosMetadataRow {
  P_STAT_CODE?: string;
  STAT_CODE?: string;
  STAT_NAME?: string;
  GRP_CODE?: string;
  GRP_NAME?: string;
  ITEM_CODE?: string;
  ITEM_NAME?: string;
  ITEM_NAME_ENG?: string;
  P_ITEM_CODE?: string;
  P_ITEM_NAME?: string;
  CYCLE?: string;
  SRCH_YN?: string;
  ORG_NAME?: string;
  UNIT_NAME?: string;
  START_TIME?: string;
  END_TIME?: string;
  DATA_CNT?: string | number;
  WEIGHT?: string | number;
  [key: string]: unknown;
}

export interface H1bMetadataCandidate extends EcosMetadataRow {
  TABLE_STAT_NAME?: string;
  MATCHED_ITEM_TERMS: string[];
}

export interface H1bMetadataDimensionGroup {
  groupCode: string;
  groupName?: string;
  options: EcosMetadataRow[];
}

export interface H1bMetadataDimensionSummary {
  statCode: string;
  statName?: string;
  cycle: "M";
  groups: H1bMetadataDimensionGroup[];
}

export interface H1bMortgageBalanceCandidateAssessment extends H1bMetadataCandidate {
  assessment: "eligible_for_semantic_review" | "near_miss";
  rejectionReasons: string[];
}

export interface H1bMortgageBalanceAssessment {
  productionRule: string;
  eligibleForSemanticReview: H1bMortgageBalanceCandidateAssessment[];
  nearMisses: H1bMortgageBalanceCandidateAssessment[];
}

export interface H1bEcosMetadataAudit {
  status: "ok" | "partial" | "error" | "unsupported_in_central_mode";
  collectedAt: string;
  purpose: string;
  guardrails: string[];
  query: {
    tableTerms: string[];
    itemTerms: string[];
    defaultTableCodes: string[];
    maxAuditTables: number;
  };
  reviewTargets: Array<{
    target: string;
    requiredSemantics: string[];
    productionRule: string;
  }>;
  tableListTotal: number | null;
  selectedTableCount: number;
  candidateCount: number;
  truncatedCandidateTables: boolean;
  selectedTables: EcosMetadataRow[];
  candidates: H1bMetadataCandidate[];
  multiDimensionalTables: H1bMetadataDimensionSummary[];
  mortgageBalanceAssessment: H1bMortgageBalanceAssessment;
  errors: Array<{ scope: string; error: string }>;
}

function normalize(value: unknown) {
  return String(value ?? "").trim().replace(/\s+/g, " ").toLocaleLowerCase("ko-KR");
}

function metadataText(row: EcosMetadataRow) {
  return [
    row.P_STAT_CODE,
    row.STAT_CODE,
    row.STAT_NAME,
    row.GRP_CODE,
    row.GRP_NAME,
    row.ITEM_CODE,
    row.ITEM_NAME,
    row.ITEM_NAME_ENG,
    row.P_ITEM_CODE,
    row.P_ITEM_NAME,
    row.CYCLE,
    row.SRCH_YN,
    row.ORG_NAME,
    row.UNIT_NAME
  ].map(normalize).join(" | ");
}

function matchedTerms(text: string, terms: readonly string[]) {
  return terms.filter((term) => text.includes(normalize(term)));
}

export function compactH1bMetadataRow(row: EcosMetadataRow): EcosMetadataRow {
  const keys = [
    "P_STAT_CODE", "STAT_CODE", "STAT_NAME", "GRP_CODE", "GRP_NAME", "ITEM_CODE", "ITEM_NAME", "ITEM_NAME_ENG",
    "P_ITEM_CODE", "P_ITEM_NAME", "CYCLE", "SRCH_YN", "ORG_NAME", "UNIT_NAME", "START_TIME", "END_TIME", "DATA_CNT", "WEIGHT"
  ] as const;
  return Object.fromEntries(keys.filter((key) => row[key] != null).map((key) => [key, row[key]]));
}

export function selectH1bCandidateTables(rows: EcosMetadataRow[]) {
  const byCode = new Map<string, { row: EcosMetadataRow; score: number; mandatory: boolean; inherited: boolean }>();
  const children = new Map<string, EcosMetadataRow[]>();
  const isSearchable = (row: EcosMetadataRow) => normalize(row.SRCH_YN) === "y" || Boolean(String(row.CYCLE ?? "").trim());

  for (const row of rows) {
    const parent = String(row.P_STAT_CODE ?? "").trim();
    if (!parent) continue;
    const list = children.get(parent) ?? [];
    list.push(row);
    children.set(parent, list);
  }

  const add = (raw: EcosMetadataRow, score: number, mandatory: boolean, inherited = false) => {
    const code = String(raw.STAT_CODE ?? "").trim();
    if (!code || (!mandatory && !isSearchable(raw))) return;
    const existing = byCode.get(code);
    if (!existing || mandatory || score > existing.score) {
      byCode.set(code, { row: compactH1bMetadataRow(raw), score, mandatory, inherited });
    }
  };

  const descendantRoots: Array<{ code: string; score: number }> = [];
  for (const raw of rows) {
    const code = String(raw.STAT_CODE ?? "").trim();
    if (!code) continue;
    const text = metadataText(raw);
    const score = matchedTerms(text, H1B_TABLE_TERMS).length;
    const mandatory = H1B_DEFAULT_TABLE_CODES.includes(code as typeof H1B_DEFAULT_TABLE_CODES[number]);
    if (score > 0 || mandatory) add(raw, score, mandatory);
    const descendantScore = matchedTerms(text, H1B_DESCENDANT_ROOT_TERMS).length;
    if (descendantScore > 0 && !isSearchable(raw)) descendantRoots.push({ code, score: descendantScore });
  }

  // StatisticTableList contains non-searchable folder rows. A keyword hit on a
  // folder such as "6.2 소비자동향조사" must be expanded to its searchable leaf
  // tables; calling StatisticItemList on the folder itself only returns INFO-200.
  // Expansion is bounded to descendants already present in the one fetched table
  // catalog and never accepts a client-supplied stat code.
  for (const root of descendantRoots) {
    const queue = [...(children.get(root.code) ?? [])];
    const visited = new Set<string>();
    while (queue.length) {
      const child = queue.shift()!;
      const code = String(child.STAT_CODE ?? "").trim();
      if (!code || visited.has(code)) continue;
      visited.add(code);
      if (isSearchable(child)) add(child, 20 + root.score, false, true);
      else queue.push(...(children.get(code) ?? []));
    }
  }

  for (const code of H1B_DEFAULT_TABLE_CODES) {
    if (!byCode.has(code)) byCode.set(code, { row: { STAT_CODE: code, STAT_NAME: "known-project-table" }, score: 0, mandatory: true, inherited: false });
  }
  const sorted = [...byCode.values()].sort((a, b) => {
    if (a.mandatory !== b.mandatory) return a.mandatory ? -1 : 1;
    if (a.inherited !== b.inherited) return a.inherited ? -1 : 1;
    if (a.score !== b.score) return b.score - a.score;
    return String(a.row.STAT_CODE).localeCompare(String(b.row.STAT_CODE));
  });
  return {
    rows: sorted.slice(0, H1B_MAX_AUDIT_TABLES).map((x) => x.row),
    truncated: sorted.length > H1B_MAX_AUDIT_TABLES,
    totalMatched: sorted.length
  };
}

export function filterH1bCandidateItems(table: EcosMetadataRow, rows: EcosMetadataRow[]): H1bMetadataCandidate[] {
  const tableName = String(table.STAT_NAME ?? "");
  return rows.flatMap((raw) => {
    const text = metadataText(raw);
    const terms = matchedTerms(text, H1B_ITEM_TERMS);
    if (!terms.length) return [];
    return [{
      ...compactH1bMetadataRow(raw),
      TABLE_STAT_NAME: tableName,
      MATCHED_ITEM_TERMS: terms
    }];
  });
}

export function summarizeH1bMonthlyDimensions(
  table: EcosMetadataRow,
  rows: EcosMetadataRow[]
): H1bMetadataDimensionSummary | null {
  const monthly = rows.filter((row) => String(row.CYCLE ?? "").trim() === "M");
  const byGroup = new Map<string, { groupName?: string; options: Map<string, EcosMetadataRow> }>();
  for (const raw of monthly) {
    const groupCode = String(raw.GRP_CODE ?? "Group1").trim() || "Group1";
    const itemCode = String(raw.ITEM_CODE ?? "").trim();
    if (!itemCode) continue;
    const current = byGroup.get(groupCode) ?? {
      groupName: String(raw.GRP_NAME ?? "").trim() || undefined,
      options: new Map<string, EcosMetadataRow>()
    };
    if (!current.options.has(itemCode)) current.options.set(itemCode, compactH1bMetadataRow(raw));
    byGroup.set(groupCode, current);
  }
  if (byGroup.size <= 1) return null;
  return {
    statCode: String(table.STAT_CODE ?? "").trim(),
    statName: String(table.STAT_NAME ?? "").trim() || undefined,
    cycle: "M",
    groups: [...byGroup.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([groupCode, value]) => ({
        groupCode,
        groupName: value.groupName,
        options: [...value.options.values()]
      }))
  };
}

export function assessH1bMortgageBalanceCandidates(
  candidates: H1bMetadataCandidate[]
): H1bMortgageBalanceAssessment {
  const productionRule = "Discovery only: even eligible rows still require semantic review before production wiring. Gate C requires an aggregate monthly deposit-bank mortgage outstanding balance compatible with 121Y015/BECBLB020202.";

  const relevant = candidates.filter((candidate) => {
    const text = `${metadataText(candidate)} | ${normalize(candidate.TABLE_STAT_NAME)}`;
    const housingRelated = text.includes(normalize("주택관련대출"));
    const mortgageBalanceLike = text.includes(normalize("주택담보"))
      && (text.includes(normalize("잔액")) || text.includes(normalize("말잔")));
    return housingRelated || mortgageBalanceLike;
  });

  const assessed = relevant.map((candidate): H1bMortgageBalanceCandidateAssessment => {
    const tableName = normalize(candidate.TABLE_STAT_NAME ?? candidate.STAT_NAME);
    const itemName = normalize(candidate.ITEM_NAME);
    const parentName = normalize(candidate.P_ITEM_NAME);
    const combined = `${tableName} | ${itemName} | ${parentName}`;
    const cycle = String(candidate.CYCLE ?? "").trim().toUpperCase();
    const unit = normalize(candidate.UNIT_NAME);
    const reasons: string[] = [];

    if (cycle !== "M") reasons.push("frequency_not_monthly");
    if (combined.includes(normalize("차주당"))) reasons.push("per_borrower_average_not_aggregate");
    if (combined.includes(normalize("주택관련대출"))) reasons.push("broader_housing_related_scope_not_mortgage_only");
    if (!combined.includes(normalize("주택담보대출"))) reasons.push("mortgage_specific_label_not_explicit");
    if (!combined.includes(normalize("예금은행"))) reasons.push("deposit_bank_scope_not_explicit");
    if (!(combined.includes(normalize("잔액")) || combined.includes(normalize("말잔")))) reasons.push("stock_balance_basis_not_explicit");
    if (!unit.includes(normalize("원")) || unit.includes("%") || unit.includes(normalize("연리"))) reasons.push("unit_not_currency_amount");

    return {
      ...candidate,
      assessment: reasons.length ? "near_miss" : "eligible_for_semantic_review",
      rejectionReasons: reasons
    };
  });

  assessed.sort((a, b) => {
    if (a.rejectionReasons.length !== b.rejectionReasons.length) return a.rejectionReasons.length - b.rejectionReasons.length;
    const codeCompare = String(a.STAT_CODE ?? "").localeCompare(String(b.STAT_CODE ?? ""));
    if (codeCompare !== 0) return codeCompare;
    const itemCompare = String(a.ITEM_CODE ?? "").localeCompare(String(b.ITEM_CODE ?? ""));
    if (itemCompare !== 0) return itemCompare;
    return String(a.CYCLE ?? "").localeCompare(String(b.CYCLE ?? ""));
  });

  return {
    productionRule,
    eligibleForSemanticReview: assessed.filter((candidate) => candidate.assessment === "eligible_for_semantic_review"),
    nearMisses: assessed.filter((candidate) => candidate.assessment === "near_miss")
  };
}

export function h1bAuditBase(collectedAt: string) {
  return {
    collectedAt,
    purpose: "H1B L0 metadata discovery only. Production wiring still requires exact semantic review of current ECOS metadata.",
    guardrails: [
      "Do not wire a candidate solely because a keyword matched.",
      "Do not substitute new-origination fixed/variable shares for existing mortgage-stock exposure.",
      "Do not substitute total outstanding household-loan rates for outstanding mortgage cost.",
      "Do not multiply balance × rate unless the populations and institution scope match.",
      "Missing exact metadata remains N/A."
    ],
    query: {
      tableTerms: [...H1B_TABLE_TERMS],
      itemTerms: [...H1B_ITEM_TERMS],
      defaultTableCodes: [...H1B_DEFAULT_TABLE_CODES],
      maxAuditTables: H1B_MAX_AUDIT_TABLES
    },
    reviewTargets: [
      {
        target: "existingMortgageCost",
        requiredSemantics: ["monthly", "outstanding/stock basis", "mortgage-specific", "interest rate", "financial-institution scope explicitly identified"],
        productionRule: "Do not substitute total outstanding loan rate or new-origination mortgage rate."
      },
      {
        target: "repricingExposure",
        requiredSemantics: ["existing mortgage stock basis", "fixed/variable/periodic repricing exposure", "share unit", "population explicitly identified"],
        productionRule: "New-origination fixed/variable shares are not acceptable."
      },
      {
        target: "mortgageBalance",
        requiredSemantics: ["outstanding/stock balance", "mortgage-specific", "population and institution scope compatible with existingMortgageCost"],
        productionRule: "Do not multiply balance × rate unless the populations match."
      },
      {
        target: "housingPriceOutlook",
        requiredSemantics: ["Housing Price Outlook CSI", "monthly", "current ECOS item metadata"],
        productionRule: "Do not substitute a broader consumer sentiment index."
      },
      {
        target: "expectedInflation",
        requiredSemantics: ["household expected inflation", "horizon explicitly identified", "monthly", "current ECOS item metadata"],
        productionRule: "Do not assume the 1Y/3Y/5Y horizon; record the exact published item."
      }
    ]
  };
}
